/**
 * Utility functions for site modal initialization and management
 */

/**
 * Extract site information from a user object with string-based site data
 * Used in UsersAssignSiteModal
 * @param {Object} user - User object with sites as a string
 * @returns {Object} Extracted site information
 */
export const extractStringBasedSiteInfo = (user) => {
  const siteNames = [];
  let messageManagerSiteNames = [];
  
  if (user && user.sites) {
    // Parse the user's sites string
    // Split by possible delimiters and extract site names
    const sections = user.sites.includes('\n') ? user.sites.split('\n') : [user.sites];
    
    sections.forEach(section => {
      const trimmedSection = section.trim();
      const colonIndex = trimmedSection.indexOf(': ');
      
      if (colonIndex !== -1) {
        // Check if this is a Message Manager section
        const prefix = trimmedSection.substring(0, colonIndex).trim();
        const sitesString = trimmedSection.substring(colonIndex + 2);
        const names = sitesString.split(',').map(s => s.trim());
        
        // Store Message Manager sites separately
        if (prefix === 'Message Manager') {
          messageManagerSiteNames = [...names];
        }
        
        siteNames.push(...names);
      }
    });
  }
  
  return { siteNames, messageManagerSiteNames };
};

/**
 * Extract site information from a user object with array-based site data
 * Used in AlertUsersAssignSiteModal
 * @param {Object} user - User object with sites as an array
 * @returns {Object} Extracted site information
 */
export const extractArrayBasedSiteInfo = (user) => {
  const siteNames = [];
  const siteIds = [];
  
  if (user && user.sites && Array.isArray(user.sites)) {
    // Get site IDs and names from user's sites array
    user.sites.forEach(site => {
      if (site.id) siteIds.push(site.id);
      if (site.name) siteNames.push(site.name);
    });
  }
  
  return { siteNames, siteIds };
};

/**
 * Find a matching CMS and sites based on site names
 * @param {Array} cmses - Array of CMS objects
 * @param {Array} siteNames - Array of site names to match
 * @param {Array} messageManagerSiteNames - Optional array of Message Manager site names to prioritize
 * @returns {Object} Object containing found CMS and site IDs
 */
export const findMatchingCmsAndSites = (cmses, siteNames, messageManagerSiteNames = []) => {
  let foundCms = null;
  let foundSites = [];
  
  // First prioritize finding Message Manager sites if available
  if (messageManagerSiteNames && messageManagerSiteNames.length > 0) {
    console.log('Prioritizing Message Manager sites:', messageManagerSiteNames);
    
    // Search through all CMSes for Message Manager sites first
    for (const cmsObj of cmses) {
      if (!cmsObj.sites || !cmsObj.siteTree) continue;
      
      // Check if any of the Message Manager site names match sites in this CMS
      const matchingSites = cmsObj.sites.filter(site => 
        messageManagerSiteNames.includes(site.name)
      );
      
      if (matchingSites.length > 0) {
        console.log('Found matching CMS for Message Manager:', cmsObj.name, 'with sites:', matchingSites);
        foundCms = cmsObj;
        
        // Mark all matching Message Manager sites as selected
        matchingSites.forEach(site => {
          if (!foundSites.includes(site.id)) {
            foundSites.push(site.id);
          }
        });
        
        break; // Stop after finding the first matching CMS with Message Manager sites
      }
    }
  }
  
  // If no Message Manager sites were found, fall back to any sites
  if (!foundCms) {
    // Search through all CMSes
    for (const cmsObj of cmses) {
      if (!cmsObj.sites || !cmsObj.siteTree) continue;
      
      // Check if any of the site names match sites in this CMS
      const matchingSites = cmsObj.sites.filter(site => 
        siteNames.includes(site.name)
      );
      
      if (matchingSites.length > 0) {
        console.log('Found matching CMS:', cmsObj.name, 'with sites:', matchingSites);
        foundCms = cmsObj;
        
        // Mark all matching sites as selected
        matchingSites.forEach(site => {
          if (!foundSites.includes(site.id)) {
            foundSites.push(site.id);
          }
        });
        
        break; // Stop after finding the first matching CMS
      }
    }
  }
  
  return { foundCms, foundSites };
};

/**
 * Check if all users have the same sites
 * @param {Array} selectedUserIds - Array of selected user IDs
 * @param {Object} dataMap - Map of user data
 * @param {Function} extractSiteInfo - Function to extract site information from a user
 * @returns {Boolean} Whether all users have the same sites
 */
export const checkIfAllUsersHaveSameSites = (selectedUserIds, dataMap, extractSiteInfo) => {
  if (!selectedUserIds || selectedUserIds.length <= 1) {
    return true;
  }
  
  // Get the first user's sites as the baseline
  const firstUserId = selectedUserIds[0];
  const firstUser = dataMap.data[firstUserId];
  
  if (!firstUser) {
    return false;
  }
  
  // Extract site information for the first user
  const { siteNames: firstUserSiteNames } = extractSiteInfo(firstUser);
  
  // Check if all other users have the same sites
  for (let i = 1; i < selectedUserIds.length; i++) {
    const userId = selectedUserIds[i];
    const user = dataMap.data[userId];
    
    if (!user) {
      return false;
    }
    
    // Extract site information for the current user
    const { siteNames: userSiteNames } = extractSiteInfo(user);
    
    // Check if the sites match the first user's sites
    if (userSiteNames.length !== firstUserSiteNames.length || 
        !userSiteNames.every(siteName => firstUserSiteNames.includes(siteName))) {
      return false;
    }
  }
  
  return true;
};

/**
 * Tree manipulation functions for site tree
 */

/**
 * Find a site in the site tree by ID
 * @param {Array} siteTree - Site tree to search
 * @param {String} siteId - Site ID to find
 * @returns {Object|null} Found site or null
 */
export const findSiteInTree = (siteTree, siteId) => {
  if (!siteTree || !Array.isArray(siteTree)) return null;
  
  for (let i = 0; i < siteTree.length; i++) {
    const site = siteTree[i];
    if (site.id === siteId) {
      return site;
    } else if (site.children && site.children.length > 0) {
      const childSite = findSiteInTree(site.children, siteId);
      if (childSite) return childSite;
    }
  }
  return null;
};

/**
 * Get all child site IDs from a site tree
 * @param {Array} siteTree - Site tree to process
 * @param {Array} childrenIds - Array to store child IDs (for recursion)
 * @returns {Array} Array of child site IDs
 */
export const getChildrenIds = (siteTree, childrenIds = []) => {
  if (!siteTree || !Array.isArray(siteTree)) return childrenIds;
  
  siteTree.forEach((site) => {
    childrenIds.push(site.id);
    if (site.children && site.children.length > 0) {
      getChildrenIds(site.children, childrenIds);
    }
  });
  return childrenIds;
};

/**
 * Close a modal by ID
 * @param {String} modalId - ID of the modal to close
 */
export const closeModal = (modalId) => {
  const modal = document.getElementById(modalId);
  if (modal) {
    const closeButton = modal.querySelector('.btn-close');
    if (closeButton) {
      closeButton.click();
    }
  }
};
